import React, { useEffect, useMemo, useState } from "react";
import CustomSelect from "../../../components/CustomSelect.jsx";
import {
    activateAdminSeason,
    archiveAdminSeason,
    createAdminRace,
    createAdminSeason,
    deleteAdminSeason,
    deleteAdminRace,
    fetchAdminSeasonRaces,
    fetchAdminSeasonRuleset,
    fetchAdminSeasons,
    fetchManagerSeriesSeasons,
    updateAdminRace,
    updateAdminSeason,
    updateAdminSeasonRuleset,
} from "../../../lib/managerApi.js";

const SEASON_STATUS_OPTIONS = [
    { value: "draft", label: "Draft" },
    { value: "active", label: "Aktiv" },
    { value: "completed", label: "Abgeschlossen" },
    { value: "archived", label: "Archiviert" },
    { value: "hidden", label: "Hidden" },
];

const RACE_STATUS_OPTIONS = [
    { value: "scheduled", label: "Geplant" },
    { value: "done", label: "Erledigt" },
    { value: "cancelled", label: "Abgesagt" },
];

const EMPTY_SEASON = {
    season_key: "",
    season_no: "",
    display_name: "",
    status: "draft",
    max_regular_starters: "60",
    start_date: "",
    end_date: "",
};

const EMPTY_RACE = {
    race_no: "",
    race_tag: "",
    track_name: "",
    race_title: "",
    race_date: "",
    briefing_from: "",
    results_from: "",
    sort_order: "0",
    status: "scheduled",
};

const DEFAULT_DRIVER_POINTS = [
    [1, 30],
    [2, 25],
    [3, 21],
    [4, 18],
    [5, 16],
    [6, 14],
    [7, 12],
    [8, 10],
    [9, 8],
    [10, 6],
    [11, 5],
    [12, 4],
    [13, 3],
    [14, 2],
    [15, 1],
];

const DEFAULT_TEAM_POINTS = [
    [1, 60],
    [2, 55],
    [3, 50],
    [4, 46],
    [5, 42],
    [6, 38],
    [7, 34],
    [8, 32],
    [9, 28],
    [10, 26],
    [11, 24],
    [12, 22],
    [13, 20],
    [14, 18],
    [15, 16],
    [16, 15],
    [17, 14],
    [18, 13],
    [19, 12],
    [20, 11],
    [21, 10],
    [22, 9],
    [23, 8],
    [24, 7],
    [25, 6],
    [26, 5],
    [27, 4],
    [28, 3],
    [29, 2],
    [30, 1],
];

function pointsRows(values) {
    return values.map(([position, points]) => ({
        position: String(position),
        points: String(points),
    }));
}

const EMPTY_RULESET = {
    ruleset_key: "gt3x_default",
    display_name: "GT3X Default Ruleset",
    status: "active",
    min_race_distance_pct: "75",
    participation_points: "0",
    participation_min_distance_pct: "0",
    driver_drop_count: "3",
    team_drop_count: "3",
    brand_drop_count: "3",
    team_top_n: "2",
    count_dsq_for_starts: true,
    count_dnf_for_points: false,
    driver_points: pointsRows(DEFAULT_DRIVER_POINTS),
    team_points: pointsRows(DEFAULT_TEAM_POINTS),
    brand_points: pointsRows(DEFAULT_TEAM_POINTS),
};

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (typeof err === "string") return err;
    if (err instanceof Error) return err.message || "Fehler";
    if (err.detail) return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
    return String(err);
}

function pad2(value) {
    return String(value).padStart(2, "0");
}

function validDateParts(year, month, day) {
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function formatDateInput(value) {
    if (!value) return "";

    const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return String(value);

    return `${match[3]}.${match[2]}.${match[1]}`;
}

function formatDateTimeInput(value) {
    if (!value) return "";

    const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
    if (!match) return String(value);

    const time = match[4] && match[5] ? ` ${match[4]}:${match[5]}` : "";
    return `${match[3]}.${match[2]}.${match[1]}${time}`;
}

function maskDateInput(value) {
    const digits = String(value ?? "").replace(/\D/g, "").slice(0, 8);
    if (!digits) return "";
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

function maskDateTimeInput(value) {
    const digits = String(value ?? "").replace(/\D/g, "").slice(0, 12);
    if (!digits) return "";

    const date = maskDateInput(digits.slice(0, 8));
    const timeDigits = digits.slice(8);
    if (!timeDigits) return date;
    if (timeDigits.length <= 2) return `${date} ${timeDigits}`;
    return `${date} ${timeDigits.slice(0, 2)}:${timeDigits.slice(2, 4)}`;
}

function parseDateForPayload(value, label) {
    const raw = String(value || "").trim();
    if (!raw) return "";

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
        const year = Number(isoMatch[1]);
        const month = Number(isoMatch[2]);
        const day = Number(isoMatch[3]);
        if (validDateParts(year, month, day)) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }

    const match = raw.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
    if (!match) throw new Error(`${label} bitte im Format dd.mm.yyyy eingeben.`);

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    if (!validDateParts(year, month, day)) throw new Error(`${label} ist kein gueltiges Datum.`);

    return `${year}-${pad2(month)}-${pad2(day)}`;
}

function parseDateTimeForPayload(value, label) {
    const raw = String(value || "").trim();
    if (!raw) return "";

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})$/);
    if (isoMatch) {
        const year = Number(isoMatch[1]);
        const month = Number(isoMatch[2]);
        const day = Number(isoMatch[3]);
        const hour = Number(isoMatch[4]);
        const minute = Number(isoMatch[5]);
        if (validDateParts(year, month, day) && hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
            return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}T${isoMatch[4]}:${isoMatch[5]}`;
        }
    }

    const match = raw.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})\s+(\d{1,2}):(\d{2})$/);
    if (!match) throw new Error(`${label} bitte im Format dd.mm.yyyy HH:mm eingeben.`);

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const hour = Number(match[4]);
    const minute = Number(match[5]);
    if (!validDateParts(year, month, day)) throw new Error(`${label} ist kein gueltiges Datum.`);
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) throw new Error(`${label} muss eine Uhrzeit im 24h-Format enthalten.`);

    return `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}`;
}

function statusLabel(status) {
    if (status === "active") return "Aktiv";
    if (status === "completed") return "Abgeschlossen";
    if (status === "archived") return "Archiviert";
    if (status === "hidden") return "Hidden";
    if (status === "done") return "Erledigt";
    if (status === "cancelled") return "Abgesagt";
    if (status === "scheduled") return "Geplant";
    return "Draft";
}

function StatusPill({ status }) {
    const cls = status === "active" || status === "scheduled"
        ? "pill pill--am"
        : status === "archived" || status === "done"
            ? "pill pill--gesamt"
            : "pill pill--int";

    return <span className={cls}>{statusLabel(status)}</span>;
}

function Field({ label, children, hint = "", required = false, className = "" }) {
    return (
        <label className={`season-field ${className}`.trim()}>
            <span className="season-field__label">
                {label}
                {required && <span aria-hidden="true"> *</span>}
            </span>
            {children}
            {hint && <span className="season-field__hint">{hint}</span>}
        </label>
    );
}

function normalizePointRows(rows, fallback) {
    const source = Array.isArray(rows) && rows.length ? rows : pointsRows(fallback);
    return source.map((row, index) => ({
        position: String(row.position ?? index + 1),
        points: String(row.points ?? 0),
    }));
}

function rulesetToForm(ruleset) {
    if (!ruleset) return { ...EMPTY_RULESET };

    return {
        ruleset_key: ruleset.ruleset_key || "gt3x_default",
        display_name: ruleset.display_name || "GT3X Default Ruleset",
        status: ruleset.status || "active",
        min_race_distance_pct: String(ruleset.min_race_distance_pct ?? 75),
        participation_points: String(ruleset.participation_points ?? 0),
        participation_min_distance_pct: String(ruleset.participation_min_distance_pct ?? 0),
        driver_drop_count: String(ruleset.driver_drop_count ?? 3),
        team_drop_count: String(ruleset.team_drop_count ?? 3),
        brand_drop_count: String(ruleset.brand_drop_count ?? 3),
        team_top_n: String(ruleset.team_top_n ?? 2),
        count_dsq_for_starts: Boolean(ruleset.count_dsq_for_starts ?? true),
        count_dnf_for_points: Boolean(ruleset.count_dnf_for_points ?? false),
        driver_points: normalizePointRows(ruleset.driver_points, DEFAULT_DRIVER_POINTS),
        team_points: normalizePointRows(ruleset.team_points, DEFAULT_TEAM_POINTS),
        brand_points: normalizePointRows(ruleset.brand_points, DEFAULT_TEAM_POINTS),
    };
}

function pointRowsPayload(rows) {
    return (rows || [])
        .map((row) => ({
            position: Number(row.position),
            points: Number(row.points),
        }))
        .filter((row) => Number.isFinite(row.position) && row.position > 0 && Number.isFinite(row.points));
}

function PointsEditor({ title, hint, rows, group, onChange }) {
    return (
        <div
            style={{
                border: "1px solid rgba(255,255,255,.10)",
                borderRadius: 14,
                padding: 12,
                background: "rgba(255,255,255,.035)",
                display: "grid",
                gap: 8,
            }}
        >
            <div>
                <strong>{title}</strong>
                {hint && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{hint}</div>}
            </div>
            <div
                className="season-points-grid"
                style={{
                    display: "grid",
                    gap: 8,
                }}
            >
                {(rows || []).map((row, index) => (
                    <label key={`${group}-${index}`} style={{ display: "grid", gap: 4 }}>
                        <span className="season-field__hint">P{row.position}</span>
                        <input
                            className="input"
                            type="number"
                            min="0"
                            value={row.points}
                            onChange={(event) => onChange(group, index, "points", event.target.value)}
                        />
                    </label>
                ))}
            </div>
        </div>
    );
}

export default function SeasonManagement() {
    const [seriesRows, setSeriesRows] = useState([]);
    const [seriesKey, setSeriesKey] = useState("gt3x");
    const [seasons, setSeasons] = useState([]);
    const [selectedSeasonId, setSelectedSeasonId] = useState("");
    const [seasonForm, setSeasonForm] = useState(EMPTY_SEASON);
    const [creatingSeason, setCreatingSeason] = useState(false);

    const [races, setRaces] = useState([]);
    const [editingRaceId, setEditingRaceId] = useState(null);
    const [raceForm, setRaceForm] = useState(EMPTY_RACE);
    const [rulesetForm, setRulesetForm] = useState(EMPTY_RULESET);
    const [rulesetExists, setRulesetExists] = useState(false);
    const [rulesetLoading, setRulesetLoading] = useState(false);
    const [rulesetSaving, setRulesetSaving] = useState(false);

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const seriesOptions = useMemo(() => {
        return seriesRows.map((series) => ({
            value: series.series_key,
            label: series.display_name,
        }));
    }, [seriesRows]);

    const selectedSeason = useMemo(() => {
        return seasons.find((season) => String(season.id) === String(selectedSeasonId)) || null;
    }, [seasons, selectedSeasonId]);

    useEffect(() => {
        let active = true;

        async function loadSeries() {
            try {
                setLoading(true);
                setError("");
                const data = await fetchManagerSeriesSeasons();
                if (!active) return;

                const rows = Array.isArray(data.series) ? data.series : [];
                setSeriesRows(rows);
                const preferred = rows.find((series) => series.series_key === "gt3x") || rows[0];
                if (preferred) setSeriesKey(preferred.series_key);
            } catch (err) {
                if (active) setError(prettyError(err));
            } finally {
                if (active) setLoading(false);
            }
        }

        loadSeries();
        return () => {
            active = false;
        };
    }, []);

    async function loadSeasons(nextSeriesKey = seriesKey) {
        if (!nextSeriesKey) return;

        try {
            setLoading(true);
            setError("");
            const data = await fetchAdminSeasons({ series_key: nextSeriesKey });
            const rows = Array.isArray(data.seasons) ? data.seasons : [];
            setSeasons(rows);

            setSelectedSeasonId((current) => {
                if (current && rows.some((season) => String(season.id) === String(current))) return current;
                const activeSeason = rows.find((season) => season.status === "active") || rows[0];
                return activeSeason?.id ? String(activeSeason.id) : "";
            });
        } catch (err) {
            setError(prettyError(err));
            setSeasons([]);
            setSelectedSeasonId("");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!seriesKey) return;
        setCreatingSeason(false);
        loadSeasons(seriesKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seriesKey]);

    useEffect(() => {
        if (creatingSeason) {
            setSeasonForm(EMPTY_SEASON);
            return;
        }

        if (!selectedSeason) {
            setSeasonForm(EMPTY_SEASON);
            return;
        }

        setSeasonForm({
            season_key: selectedSeason.season_key || "",
            season_no: selectedSeason.season_no ? String(selectedSeason.season_no) : "",
            display_name: selectedSeason.display_name || "",
            status: selectedSeason.status || "draft",
            max_regular_starters: selectedSeason.max_regular_starters ? String(selectedSeason.max_regular_starters) : "60",
            start_date: formatDateInput(selectedSeason.start_date),
            end_date: formatDateInput(selectedSeason.end_date),
        });
    }, [selectedSeason, creatingSeason]);

    async function loadRaces() {
        if (!selectedSeasonId) {
            setRaces([]);
            return;
        }

        try {
            setError("");
            const data = await fetchAdminSeasonRaces(selectedSeasonId);
            setRaces(Array.isArray(data.races) ? data.races : []);
        } catch (err) {
            setError(prettyError(err));
            setRaces([]);
        }
    }

    async function loadRuleset() {
        if (!selectedSeasonId || creatingSeason) {
            setRulesetForm(EMPTY_RULESET);
            setRulesetExists(false);
            return;
        }

        try {
            setRulesetLoading(true);
            setError("");
            const data = await fetchAdminSeasonRuleset(selectedSeasonId);
            setRulesetForm(rulesetToForm(data.ruleset));
            setRulesetExists(Boolean(data.exists));
        } catch (err) {
            setError(prettyError(err));
            setRulesetForm(EMPTY_RULESET);
            setRulesetExists(false);
        } finally {
            setRulesetLoading(false);
        }
    }

    useEffect(() => {
        loadRaces();
        loadRuleset();
        resetRaceForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSeasonId, creatingSeason]);

    function resetRaceForm() {
        setEditingRaceId(null);
        setRaceForm(EMPTY_RACE);
    }

    function startRaceEdit(race) {
        setEditingRaceId(race.id);
        setRaceForm({
            race_no: race.race_no ? String(race.race_no) : "",
            race_tag: race.race_tag || "",
            track_name: race.track_name || "",
            race_title: race.race_title || "",
            race_date: formatDateTimeInput(race.race_date),
            briefing_from: formatDateTimeInput(race.briefing_from),
            results_from: formatDateTimeInput(race.results_from),
            sort_order: race.sort_order ? String(race.sort_order) : "0",
            status: race.status || "scheduled",
        });
    }

    function seasonPayload() {
        return {
            series_key: seriesKey,
            season_key: seasonForm.season_key,
            season_no: Number(seasonForm.season_no),
            display_name: seasonForm.display_name,
            status: seasonForm.status,
            max_regular_starters: Number(seasonForm.max_regular_starters || 60),
            start_date: parseDateForPayload(seasonForm.start_date, "Startdatum"),
            end_date: parseDateForPayload(seasonForm.end_date, "Enddatum"),
        };
    }

    function racePayload() {
        return {
            race_no: Number(raceForm.race_no),
            race_tag: raceForm.race_tag,
            track_name: raceForm.track_name,
            race_title: raceForm.race_title,
            race_date: parseDateTimeForPayload(raceForm.race_date, "Rennen ab"),
            briefing_from: parseDateTimeForPayload(raceForm.briefing_from, "Briefing sichtbar ab"),
            results_from: parseDateTimeForPayload(raceForm.results_from, "Results sichtbar ab"),
            sort_order: Number(raceForm.sort_order || 0),
            status: raceForm.status,
        };
    }

    function rulesetPayload() {
        return {
            ruleset_key: rulesetForm.ruleset_key,
            display_name: rulesetForm.display_name,
            status: rulesetForm.status,
            min_race_distance_pct: Number(rulesetForm.min_race_distance_pct || 0),
            participation_points: Number(rulesetForm.participation_points || 0),
            participation_min_distance_pct: Number(rulesetForm.participation_min_distance_pct || 0),
            driver_drop_count: Number(rulesetForm.driver_drop_count || 0),
            team_drop_count: Number(rulesetForm.team_drop_count || 0),
            brand_drop_count: Number(rulesetForm.brand_drop_count || 0),
            team_top_n: Number(rulesetForm.team_top_n || 1),
            count_dsq_for_starts: Boolean(rulesetForm.count_dsq_for_starts),
            count_dnf_for_points: Boolean(rulesetForm.count_dnf_for_points),
            driver_points: pointRowsPayload(rulesetForm.driver_points),
            team_points: pointRowsPayload(rulesetForm.team_points),
            brand_points: pointRowsPayload(rulesetForm.brand_points),
        };
    }

    function updateRulesetField(name, value) {
        setRulesetForm((prev) => ({ ...prev, [name]: value }));
    }

    function updatePointRow(group, index, field, value) {
        setRulesetForm((prev) => {
            const rows = [...(prev[group] || [])];
            rows[index] = { ...rows[index], [field]: value };
            return { ...prev, [group]: rows };
        });
    }

    function resetRulesetDefaults() {
        setRulesetForm(EMPTY_RULESET);
    }

    async function saveRuleset() {
        if (!selectedSeasonId) return;
        if (!rulesetForm.ruleset_key || !rulesetForm.display_name) {
            setError("Bitte Ruleset Key und Namen ausfuellen.");
            return;
        }

        try {
            setRulesetSaving(true);
            setError("");
            setSuccess("");
            const data = await updateAdminSeasonRuleset(selectedSeasonId, rulesetPayload());
            setRulesetForm(rulesetToForm(data.ruleset));
            setRulesetExists(true);
            setSuccess("Ruleset wurde gespeichert.");
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setRulesetSaving(false);
        }
    }

    async function saveSeason() {
        if (!seasonForm.season_key || !seasonForm.season_no || !seasonForm.display_name) {
            setError("Bitte Season Key, Nummer und Namen ausfuellen.");
            return;
        }

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const payload = seasonPayload();
            const data = creatingSeason
                ? await createAdminSeason(payload)
                : await updateAdminSeason(selectedSeasonId, payload);

            setSuccess(creatingSeason ? "Saison wurde angelegt." : "Saison wurde gespeichert.");
            setCreatingSeason(false);
            await loadSeasons(seriesKey);
            if (data.season?.id) setSelectedSeasonId(String(data.season.id));
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function activateSeason() {
        if (!selectedSeasonId) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await activateAdminSeason(selectedSeasonId);
            setSuccess("Saison wurde aktiviert. Andere aktive Saisons dieser Serie wurden archiviert.");
            await loadSeasons(seriesKey);
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function archiveSeason() {
        if (!selectedSeasonId) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await archiveAdminSeason(selectedSeasonId);
            setSuccess("Saison wurde archiviert.");
            await loadSeasons(seriesKey);
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function removeSeason() {
        if (!selectedSeasonId || !selectedSeason) return;
        if (!window.confirm(`Saison '${selectedSeason.display_name}' wirklich loeschen?`)) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await deleteAdminSeason(selectedSeasonId);
            setSuccess("Saison wurde geloescht.");
            setSelectedSeasonId("");
            await loadSeasons(seriesKey);
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function saveRace() {
        if (!selectedSeasonId || !raceForm.race_no || !raceForm.race_tag || !raceForm.track_name) {
            setError("Bitte Race Nummer, Race Tag und Strecke ausfuellen.");
            return;
        }

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const payload = racePayload();
            if (editingRaceId) {
                await updateAdminRace(editingRaceId, payload);
                setSuccess("Rennen wurde gespeichert.");
            } else {
                await createAdminRace(selectedSeasonId, payload);
                setSuccess("Rennen wurde angelegt.");
            }
            resetRaceForm();
            await loadRaces();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function removeRace(raceId) {
        if (!window.confirm("Rennen wirklich loeschen?")) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await deleteAdminRace(raceId);
            setSuccess("Rennen wurde geloescht.");
            await loadRaces();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="rv-page manager-modern-page season-management-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">Admin</div>
                        <h1 className="rv-page-title">Saisonverwaltung</h1>
                        <p className="rv-page-subtitle">
                            Saisons anlegen, aktivieren, archivieren und den Schedule einer Saison pflegen.
                        </p>
                    </div>
                </div>
            </section>

            {error && <div className="rv-message rv-message--error">{error}</div>}
            {success && <div className="rv-message rv-message--ok">{success}</div>}

            <div className="card rv-filter-card" style={{ display: "grid", gap: 14 }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: 12,
                    }}
                >
                    <Field label="Serie">
                        <CustomSelect
                            value={seriesKey}
                            onChange={setSeriesKey}
                            options={seriesOptions}
                            allowEmpty={false}
                        />
                    </Field>
                    <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap" }}>
                        <button className="btn btn--accent" type="button" onClick={() => setCreatingSeason(true)} disabled={busy}>
                            Neue Saison
                        </button>
                        <button className="btn" type="button" onClick={() => loadSeasons(seriesKey)} disabled={loading}>
                            Aktualisieren
                        </button>
                    </div>
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
                    gap: 16,
                    alignItems: "start",
                }}
            >
                <div className="card" style={{ display: "grid", gap: 10 }}>
                    <h2 style={{ marginTop: 0 }}>Saisons</h2>
                    {!seasons.length && <div className="muted">Keine Saisons vorhanden.</div>}
                    {seasons.map((season) => {
                        const active = String(season.id) === String(selectedSeasonId) && !creatingSeason;
                        return (
                            <button
                                key={season.id}
                                type="button"
                                onClick={() => {
                                    setCreatingSeason(false);
                                    setSelectedSeasonId(String(season.id));
                                }}
                                style={{
                                    textAlign: "left",
                                    border: active
                                        ? "1px solid rgba(228,254,60,.46)"
                                        : "1px solid rgba(255,255,255,.10)",
                                    background: active
                                        ? "rgba(228,254,60,.10)"
                                        : "rgba(255,255,255,.035)",
                                    borderRadius: 14,
                                    padding: 12,
                                    color: "var(--text)",
                                    cursor: "pointer",
                                    display: "grid",
                                    gap: 6,
                                }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                    <strong>{season.display_name}</strong>
                                    <StatusPill status={season.status} />
                                </div>
                                <div className="muted" style={{ fontSize: 13 }}>
                                    {season.season_key} / Season {season.season_no} / Starter {season.max_regular_starters}
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="card" style={{ display: "grid", gap: 18 }}>
                    <section style={{ display: "grid", gap: 12 }}>
                        <div>
                            <div className="rv-page-kicker">{creatingSeason ? "Neue Saison" : "Saison bearbeiten"}</div>
                            <h2 style={{ margin: "4px 0 0" }}>
                                {creatingSeason ? "Saison anlegen" : selectedSeason?.display_name || "Keine Saison ausgewaehlt"}
                            </h2>
                        </div>

                        <div className="season-management-form-grid">
                            <Field
                                label="Season Key"
                                required
                                hint="Kurzkey fuer API/URLs, z. B. s3."
                            >
                                <input
                                    className="input"
                                    value={seasonForm.season_key}
                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, season_key: event.target.value }))}
                                    placeholder="z. B. s3"
                                />
                            </Field>

                            <Field label="Season Nummer" required hint="Muss pro Serie eindeutig sein.">
                                <input
                                    className="input"
                                    type="number"
                                    min="1"
                                    value={seasonForm.season_no}
                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, season_no: event.target.value }))}
                                />
                            </Field>

                            <Field label="Name" required className="season-field--wide">
                                <input
                                    className="input"
                                    value={seasonForm.display_name}
                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, display_name: event.target.value }))}
                                    placeholder="Season 3"
                                />
                            </Field>

                            <Field label="Status" hint="Aktivieren archiviert andere aktive Saisons dieser Serie.">
                                <CustomSelect
                                    value={seasonForm.status}
                                    onChange={(value) => setSeasonForm((prev) => ({ ...prev, status: value }))}
                                    options={SEASON_STATUS_OPTIONS}
                                    allowEmpty={false}
                                />
                            </Field>

                            <Field label="Max Starter" hint="Grenze fuer Regular-Grid in Abmeldungen.">
                                <input
                                    className="input"
                                    type="number"
                                    min="1"
                                    value={seasonForm.max_regular_starters}
                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, max_regular_starters: event.target.value }))}
                                />
                            </Field>

                            <Field label="Startdatum" hint="Optional, Format: dd.mm.yyyy">
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="dd.mm.yyyy"
                                    value={seasonForm.start_date}
                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, start_date: maskDateInput(event.target.value) }))}
                                />
                            </Field>

                            <Field label="Enddatum" hint="Optional, Format: dd.mm.yyyy">
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="dd.mm.yyyy"
                                    value={seasonForm.end_date}
                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, end_date: maskDateInput(event.target.value) }))}
                                />
                            </Field>
                        </div>

                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <button className="btn btn--accent" type="button" onClick={saveSeason} disabled={busy || (!creatingSeason && !selectedSeasonId)}>
                                {creatingSeason ? "Saison anlegen" : "Saison speichern"}
                            </button>
                            {!creatingSeason && selectedSeasonId && (
                                <>
                                    <button className="btn" type="button" onClick={activateSeason} disabled={busy}>
                                        Aktivieren
                                    </button>
                                    <button className="btn" type="button" onClick={archiveSeason} disabled={busy}>
                                        Archivieren
                                    </button>
                                    <button className="btn" type="button" onClick={removeSeason} disabled={busy}>
                                        Saison loeschen
                                    </button>
                                </>
                            )}
                        </div>
                    </section>

                    {!creatingSeason && selectedSeasonId && (
                        <section style={{ display: "grid", gap: 12 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                                <div>
                                    <div className="rv-page-kicker">Ruleset</div>
                                    <h3 style={{ margin: "4px 0 0" }}>Punkte & Wertungsregeln</h3>
                                    <p className="muted" style={{ margin: "6px 0 0" }}>
                                        Fahrer punkten nach Klassenposition. Team- und Markenwertung nutzen die Gesamtposition des Rennens.
                                    </p>
                                </div>
                                <StatusPill status={rulesetExists ? "active" : "draft"} />
                            </div>

                            {rulesetLoading ? (
                                <div className="muted">Ruleset wird geladen...</div>
                            ) : (
                                <>
                                    <div className="season-management-form-grid">
                                        <Field label="Ruleset Key" required hint="Eindeutiger Key innerhalb der Saison.">
                                            <input
                                                className="input"
                                                value={rulesetForm.ruleset_key}
                                                onChange={(event) => updateRulesetField("ruleset_key", event.target.value)}
                                            />
                                        </Field>

                                        <Field label="Ruleset Name" required className="season-field--wide">
                                            <input
                                                className="input"
                                                value={rulesetForm.display_name}
                                                onChange={(event) => updateRulesetField("display_name", event.target.value)}
                                            />
                                        </Field>

                                        <Field label="Dropweeks Fahrer">
                                            <input
                                                className="input"
                                                type="number"
                                                min="0"
                                                value={rulesetForm.driver_drop_count}
                                                onChange={(event) => updateRulesetField("driver_drop_count", event.target.value)}
                                            />
                                        </Field>

                                        <Field label="Dropweeks Team">
                                            <input
                                                className="input"
                                                type="number"
                                                min="0"
                                                value={rulesetForm.team_drop_count}
                                                onChange={(event) => updateRulesetField("team_drop_count", event.target.value)}
                                            />
                                        </Field>

                                        <Field label="Dropweeks Marke">
                                            <input
                                                className="input"
                                                type="number"
                                                min="0"
                                                value={rulesetForm.brand_drop_count}
                                                onChange={(event) => updateRulesetField("brand_drop_count", event.target.value)}
                                            />
                                        </Field>

                                        <Field label="Teamfahrer pro Rennen" hint="Nur die bestplatzierten N Fahrer zaehlen.">
                                            <input
                                                className="input"
                                                type="number"
                                                min="1"
                                                value={rulesetForm.team_top_n}
                                                onChange={(event) => updateRulesetField("team_top_n", event.target.value)}
                                            />
                                        </Field>

                                        <Field label="Mindestdistanz %" hint="Rennanteil fuer Punkteberechtigung.">
                                            <input
                                                className="input"
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={rulesetForm.min_race_distance_pct}
                                                onChange={(event) => updateRulesetField("min_race_distance_pct", event.target.value)}
                                            />
                                        </Field>

                                        <Field label="Participation Points" hint="Aktuell 0, kann spaeter aktiviert werden.">
                                            <input
                                                className="input"
                                                type="number"
                                                min="0"
                                                value={rulesetForm.participation_points}
                                                onChange={(event) => updateRulesetField("participation_points", event.target.value)}
                                            />
                                        </Field>

                                        <Field label="PP Mindestdistanz %" hint="Nur relevant, wenn PP > 0.">
                                            <input
                                                className="input"
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={rulesetForm.participation_min_distance_pct}
                                                onChange={(event) => updateRulesetField("participation_min_distance_pct", event.target.value)}
                                            />
                                        </Field>
                                    </div>

                                    <div style={{ display: "grid", gap: 12 }}>
                                        <PointsEditor
                                            title="Fahrer-/Klassenpunkte"
                                            hint="PRO, INT und AM: Position innerhalb der Klasse."
                                            rows={rulesetForm.driver_points}
                                            group="driver_points"
                                            onChange={updatePointRow}
                                        />
                                        <PointsEditor
                                            title="Teampunkte"
                                            hint="Position im Gesamtergebnis."
                                            rows={rulesetForm.team_points}
                                            group="team_points"
                                            onChange={updatePointRow}
                                        />
                                        <PointsEditor
                                            title="Markenpunkte"
                                            hint="Aktuell identisch zu Teampunkten, ebenfalls Gesamtposition."
                                            rows={rulesetForm.brand_points}
                                            group="brand_points"
                                            onChange={updatePointRow}
                                        />
                                    </div>

                                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                        <button className="btn btn--accent" type="button" onClick={saveRuleset} disabled={busy || rulesetSaving}>
                                            {rulesetExists ? "Ruleset speichern" : "Default-Ruleset anlegen"}
                                        </button>
                                        <button className="btn" type="button" onClick={resetRulesetDefaults} disabled={busy || rulesetSaving}>
                                            Defaults einsetzen
                                        </button>
                                    </div>
                                    <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                                        Default-Ruleset anlegen speichert die aktuell sichtbaren Default-Werte erstmals fuer diese Saison.
                                        Defaults einsetzen ersetzt nur das Formular lokal mit den Standardwerten; gespeichert wird erst mit dem Speichern-Button.
                                    </p>
                                </>
                            )}
                        </section>
                    )}

                    {!creatingSeason && selectedSeasonId && (
                        <section style={{ display: "grid", gap: 12 }}>
                            <h3 style={{ margin: 0 }}>Schedule / Rennen</h3>

                            <div className="season-management-race-grid">
                                <Field label="Race Nummer" required>
                                    <input
                                        className="input"
                                        type="number"
                                        min="1"
                                        value={raceForm.race_no}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, race_no: event.target.value }))}
                                    />
                                </Field>

                                <Field label="Race Tag" required hint="Kurzname, z. B. R1.">
                                    <input
                                        className="input"
                                        value={raceForm.race_tag}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, race_tag: event.target.value }))}
                                        placeholder="R1"
                                    />
                                </Field>

                                <Field label="Strecke" required className="season-field--wide">
                                    <input
                                        className="input"
                                        value={raceForm.track_name}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, track_name: event.target.value }))}
                                    />
                                </Field>

                                <Field label="Titel" className="season-field--wide" hint="Optionaler Anzeigename.">
                                    <input
                                        className="input"
                                        value={raceForm.race_title}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, race_title: event.target.value }))}
                                        placeholder="Optional"
                                    />
                                </Field>

                                <Field label="Rennen ab" hint="Format: dd.mm.yyyy HH:mm, 24h">
                                    <input
                                        className="input"
                                        type="text"
                                        placeholder="dd.mm.yyyy HH:mm"
                                        value={raceForm.race_date}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, race_date: maskDateTimeInput(event.target.value) }))}
                                    />
                                </Field>

                                <Field label="Briefing sichtbar ab" hint="Steuert Auto-Race-Zeitraum. Format: dd.mm.yyyy HH:mm, 24h">
                                    <input
                                        className="input"
                                        type="text"
                                        placeholder="dd.mm.yyyy HH:mm"
                                        value={raceForm.briefing_from}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, briefing_from: maskDateTimeInput(event.target.value) }))}
                                    />
                                </Field>

                                <Field label="Results sichtbar ab" hint="Ende des Auto-Race-Zeitraums. Format: dd.mm.yyyy HH:mm, 24h">
                                    <input
                                        className="input"
                                        type="text"
                                        placeholder="dd.mm.yyyy HH:mm"
                                        value={raceForm.results_from}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, results_from: maskDateTimeInput(event.target.value) }))}
                                    />
                                </Field>

                                <Field label="Sortierung">
                                    <input
                                        className="input"
                                        type="number"
                                        min="0"
                                        value={raceForm.sort_order}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, sort_order: event.target.value }))}
                                    />
                                </Field>

                                <Field label="Status">
                                    <CustomSelect
                                        value={raceForm.status}
                                        onChange={(value) => setRaceForm((prev) => ({ ...prev, status: value }))}
                                        options={RACE_STATUS_OPTIONS}
                                        allowEmpty={false}
                                    />
                                </Field>
                            </div>

                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <button className="btn btn--accent" type="button" onClick={saveRace} disabled={busy}>
                                    {editingRaceId ? "Rennen speichern" : "Rennen anlegen"}
                                </button>
                                {editingRaceId && (
                                    <button className="btn" type="button" onClick={resetRaceForm} disabled={busy}>
                                        Bearbeitung abbrechen
                                    </button>
                                )}
                            </div>

                            <div style={{ display: "grid", gap: 8 }}>
                                {!races.length && <div className="muted">Noch keine Rennen fuer diese Saison.</div>}
                                {races.map((race) => (
                                    <div
                                        key={race.id}
                                        style={{
                                            border: "1px solid rgba(255,255,255,.10)",
                                            borderRadius: 14,
                                            padding: 12,
                                            background: "rgba(255,255,255,.035)",
                                            display: "grid",
                                            gap: 8,
                                        }}
                                    >
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                                            <strong>{race.race_tag} - {race.track_name}</strong>
                                            <StatusPill status={race.status} />
                                        </div>
                                        <div className="muted" style={{ fontSize: 13 }}>
                                            Race #{race.race_no} / Sort {race.sort_order}
                                            {race.race_date ? ` / ${formatDateTimeInput(race.race_date)}` : ""}
                                        </div>
                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                            <button className="btn" type="button" onClick={() => startRaceEdit(race)} disabled={busy}>
                                                Bearbeiten
                                            </button>
                                            <button className="btn" type="button" onClick={() => removeRace(race.id)} disabled={busy}>
                                                Loeschen
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}
